import { pick } from "es-toolkit";
import { useMemo, type ComponentProps } from "react";

import { Select } from "@skilitics/designer-ui-components";

import { useContextNodeId } from "../../navigation";
import { useGetById } from "../../state/contextualSelectors";
import { useGetInvokableActions } from "./getInvokableActions";
import {
  nodeActionIdStringToActionId,
  nodeActionIdToIdString,
  type InvokableAction,
  type InvokableActionIdString,
} from "./shared";

type ActionOption = Omit<InvokableAction, "title">;

type Props = Omit<ComponentProps<typeof Select>, "value" | "onChange" | "options"> & {
  onChange: (action?: ActionOption | undefined) => void;
  value: ActionOption | undefined;
  noActionLabel?: string;
  excludePayloadRequired?: boolean;
};

export const AvailableActionsPicker = ({
  onChange,
  value,
  disabled,
  noActionLabel,
  excludePayloadRequired,
  ...selectProps
}: Props) => {
  const contextNodeId = useContextNodeId();
  const getById = useGetById();
  const getInvokableActions = useGetInvokableActions();

  const availableActions = useMemo(() => {
    if (!contextNodeId) return [];
    const contextNodeRole = getById(contextNodeId)?.role;
    const actions = getInvokableActions(contextNodeId, { excludePayloadRequired });

    // Restrict interludes from submitting the activity.
    if (contextNodeRole === "interlude") {
      return actions.filter((action) => action.type !== "finalize");
    }
    return actions;
  }, [contextNodeId, excludePayloadRequired, getInvokableActions, getById]);

  const options = useMemo(() => {
    return [
      {
        label: noActionLabel || "No action",
        value: "",
      },
      ...availableActions.map((action) => ({
        label: action.title ?? "",
        value: nodeActionIdToIdString(action),
      })),
    ];
  }, [availableActions, noActionLabel]);

  const currentValue = options.find((option) => option.value === nodeActionIdToIdString(value))
    ? nodeActionIdToIdString(value)
    : "";

  const handleChange: ComponentProps<typeof Select>["onChange"] = (e) => {
    const selectedValue = e.target.value as InvokableActionIdString;
    if (!selectedValue) {
      onChange?.();
    } else {
      const { nodeId, name, id } = nodeActionIdStringToActionId(selectedValue);
      const selectedAction = availableActions.find(
        (action) => action.nodeId === nodeId && action.name === name && action.id === id,
      );
      if (selectedAction)
        onChange?.(pick(selectedAction, ["name", "type", "payload", "nodeId", "id", "payloadNodeRefProps"]));
    }
  };

  return <Select {...selectProps} options={options} value={currentValue} onChange={handleChange} disabled={disabled} />;
};
